import { useEffect, useMemo, useState } from "react";
import API_BASE from "../../../../config";
import { authHeaders, fmtDateShort } from "../profileUtils";
import { TabSpinner, TabError, TabEmpty } from "./FamilyTab";
import SectionProfileModal  from "../../SectionProfileModal/SectionProfileModal";
import StudentProfileModal  from "../StudentProfileModal";
import s from "./AcademicHistoryTab.module.css";
import { syLabel } from "../../../../utils/schoolYear";

const STATUS_STYLE = {
  ENROLLED:  { bg: "#e8f6ed", color: "#1e6f3e" },
  COMPLETED: { bg: "#e8eef9", color: "#2b4d8a" },
  DROPPED:   { bg: "#fde8e4", color: "#c0392b" },
};

function empName(emp) {
  if (!emp) return null;
  return [emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(" ");
}

export default function AcademicHistoryTab({
  studentId,
  sections     = [],
  gradeLevels  = [],
  schoolYears  = [],
  semesters    = [],
  parentZIndex = 1090,
}) {
  const [enrollments, setEnrollments] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  /* id → employee — fetched once for adviser + teacher name resolution */
  const [employees,   setEmployees]   = useState({});
  /* Set of enrollment IDs that are expanded */
  const [expandedRows, setExpandedRows] = useState(new Set());
  /* cacheKey → { loading, room, offerings, subjectMap } */
  const [expandCache, setExpandCache] = useState({});

  /* ── Enrollments ─────────────────────────────────────────────────── */
  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true); setError("");
    fetch(`${API_BASE}/api/enrollments?studentId=${studentId}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => { if (!cancelled) setEnrollments(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setError("Could not load enrollment history."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId]);

  /* ── Employees — loaded once for name resolution ─────────────────── */
  useEffect(() => {
    fetch(`${API_BASE}/api/employees?all=true`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (Array.isArray(data))
          setEmployees(Object.fromEntries(data.map(e => [e.id, e])));
      })
      .catch(() => {});
  }, []);

  /* ── Lookup maps ─────────────────────────────────────────────────── */
  const sectionById    = useMemo(() => Object.fromEntries(sections.map(sec => [sec.id, sec])),    [sections]);
  const gradeLevelById = useMemo(() => Object.fromEntries(gradeLevels.map(g => [g.id, g])), [gradeLevels]);
  const syById         = useMemo(() => Object.fromEntries(schoolYears.map(y => [y.id, y])), [schoolYears]);
  const semById        = useMemo(() => Object.fromEntries(semesters.map(m => [m.id, m])),   [semesters]);

  /* ── Group by school year, newest first ─────────────────────────── */
  const grouped = useMemo(() => {
    const map = new Map();
    [...enrollments]
      .sort((a, b) => (b.id || 0) - (a.id || 0))
      .forEach(e => {
        const key = e.schoolYearId ?? "unknown";
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(e);
      });
    return [...map.entries()];
  }, [enrollments]);

  /* ── Section modal ───────────────────────────────────────────────── */
  const [sectionModalSec,   setSectionModalSec]   = useState(null);
  /* Student profile opened from clicking a student inside the section roster */
  const [profileStudent,    setProfileStudent]    = useState(null);

  /* ── Expand helpers ──────────────────────────────────────────────── */
  function makeCacheKey(e, sec) {
    return `${sec?.id ?? "none"}-${e.semesterId ?? "all"}`;
  }

  function loadExpandData(e, sec) {
    const key = makeCacheKey(e, sec);
    if (expandCache[key]) return;

    setExpandCache(prev => ({ ...prev, [key]: { loading: true } }));

    Promise.all([
      sec?.homeroomId
        ? fetch(`${API_BASE}/api/rooms/${sec.homeroomId}`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : null).catch(() => null)
        : Promise.resolve(null),
      sec?.id
        ? fetch(
            `${API_BASE}/api/class-offerings?sectionId=${sec.id}${e.semesterId ? `&semesterId=${e.semesterId}` : ""}`,
            { headers: authHeaders() }
          ).then(r => r.ok ? r.json() : []).catch(() => [])
        : Promise.resolve([]),
      fetch(`${API_BASE}/api/subjects`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([room, offerings, subjects]) => {
      const subjectMap = Object.fromEntries(
        (Array.isArray(subjects) ? subjects : []).map(sub => [sub.id, sub])
      );
      setExpandCache(prev => ({
        ...prev,
        [key]: {
          loading: false,
          room,
          offerings: Array.isArray(offerings) ? offerings : [],
          subjectMap,
        },
      }));
    });
  }

  function handleRowClick(e, sec) {
    const isOpen = expandedRows.has(e.id);
    setExpandedRows(prev => {
      const next = new Set(prev);
      isOpen ? next.delete(e.id) : next.add(e.id);
      return next;
    });
    if (!isOpen) loadExpandData(e, sec);
  }

  /* ── Render ──────────────────────────────────────────────────────── */
  if (loading) return <TabSpinner label="Loading enrollment history…" />;
  if (error)   return <TabError message={error} />;
  if (enrollments.length === 0) return (
    <TabEmpty
      icon="📚"
      title="No enrollment records"
      sub="This student has not been enrolled in any semester yet."
    />
  );

  return (
    <div className={s.root}>
      <div className={s.countLine}>
        {enrollments.length} enrollment record{enrollments.length !== 1 ? "s" : ""}
      </div>

      <div className={s.timeline}>
        {grouped.map(([syId, rows], gIdx) => {
          const sy = syById[syId];
          return (
            <div key={syId} className={s.group} style={{ animationDelay: `${gIdx * 60}ms` }}>
              <div className={s.groupHead}>
                <span className={s.syDot} aria-hidden="true" />
                <span className={s.syLabel}>
                  {sy ? syLabel(sy.label) : "Unknown School Year"}
                </span>
              </div>

              <div className={s.rows}>
                {rows.map(e => {
                  const sec    = e.sectionId   ? sectionById[e.sectionId]          : null;
                  const grade  = sec?.gradeLevelId ? gradeLevelById[sec.gradeLevelId] : null;
                  const sem    = e.semesterId  ? semById[e.semesterId]              : null;
                  const badge  = STATUS_STYLE[e.status] || { bg: "#f0f0f0", color: "#888" };
                  const open   = expandedRows.has(e.id);
                  const cached = expandCache[makeCacheKey(e, sec)];
                  const adviser = sec?.adviserStaffId ? employees[sec.adviserStaffId] : null;

                  return (
                    <div key={e.id} className={s.enrollmentEntry}>
                      <div
                        className={s.row}
                        onClick={() => handleRowClick(e, sec)}
                        onKeyDown={ev => {
                          if (ev.key === "Enter" || ev.key === " ") {
                            ev.preventDefault();
                            handleRowClick(e, sec);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-expanded={open}>

                        <div className={s.rowMain}>
                          <div className={s.rowSection}>
                            {grade && <span className={s.gradeChip}>{grade.name}</span>}
                            {sec   && <span className={s.secName}>{sec.name}</span>}
                            {!sec  && <span className={s.muted}>Section unassigned</span>}
                          </div>
                          <div className={s.rowMeta}>
                            {sem?.label    && <span>{sem.label}</span>}
                            {e.enrolledAt  && <span>Enrolled {fmtDateShort(e.enrolledAt)}</span>}
                          </div>
                        </div>

                        <div className={s.rowRight}>
                          <span
                            className={s.statusBadge}
                            style={{ background: badge.bg, color: badge.color }}>
                            {e.status || "—"}
                          </span>
                          <span
                            className={`${s.rowChevron} ${open ? s.chevronOpen : ""}`}
                            aria-hidden="true">
                            ›
                          </span>
                        </div>
                      </div>

                      {open && (
                        <div className={s.expandPanel}>
                          {!cached || cached.loading ? (
                            <div className={s.expandLoading}>Loading section details…</div>
                          ) : (
                              <EnrollmentDetail
                              sec={sec}
                              cached={cached}
                              employees={employees}
                              adviser={adviser}
                              onOpenSection={() => setSectionModalSec(sec)}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <SectionProfileModal
        open={!!sectionModalSec}
        section={sectionModalSec}
        gradeLevels={gradeLevels}
        schoolYears={schoolYears}
        semesters={semesters}
        zIndex={parentZIndex + 5}
        onClose={() => setSectionModalSec(null)}
        onViewStudent={st => { setSectionModalSec(null); setProfileStudent(st); }}
      />

      {/* Student profile opened from clicking a student in the section's Students tab */}
      <StudentProfileModal
        open={!!profileStudent}
        student={profileStudent}
        sections={sections}
        gradeLevels={gradeLevels}
        schoolYears={schoolYears}
        semesters={semesters}
        zIndex={parentZIndex + 10}
        onClose={() => setProfileStudent(null)}
      />
    </div>
  );
}

/* ── Expanded detail panel ───────────────────────────────────────────── */

function EnrollmentDetail({ sec, cached, employees, adviser, onOpenSection }) {
  const { room, offerings = [], subjectMap = {} } = cached;
  const hasContent = room || adviser || offerings.length > 0;

  return (
    <div className={s.detailBody}>

      {/* Homeroom */}
      {room && (
        <div className={s.detailSection}>
          <div className={s.detailHeading}>
            <span aria-hidden="true">📍</span> Homeroom
          </div>
          <div className={s.detailGrid}>
            <span className={s.detailKey}>Room</span>
            <span className={s.detailVal}>{room.name}</span>
            {room.building && (
              <>
                <span className={s.detailKey}>Building</span>
                <span className={s.detailVal}>{room.building}</span>
              </>
            )}
            {room.floor != null && (
              <>
                <span className={s.detailKey}>Floor</span>
                <span className={s.detailVal}>{room.floor}</span>
              </>
            )}
            {room.roomType && (
              <>
                <span className={s.detailKey}>Type</span>
                <span className={s.detailVal}>{room.roomType}</span>
              </>
            )}
            {room.capacity && (
              <>
                <span className={s.detailKey}>Capacity</span>
                <span className={s.detailVal}>{room.capacity} seats</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Class adviser */}
      {adviser && (
        <div className={s.detailSection}>
          <div className={s.detailHeading}>
            <span aria-hidden="true">👤</span> Class Adviser
          </div>
          <div className={s.adviserName}>{empName(adviser)}</div>
        </div>
      )}

      {/* Subjects & schedule */}
      {offerings.length > 0 && (
        <div className={s.detailSection}>
          <div className={s.detailHeading}>
            <span aria-hidden="true">📚</span> Subjects &amp; Schedule
          </div>
          <div className={s.offeringsTable}>
            {offerings.map(o => {
              const subject = subjectMap[o.subjectId];
              const teacher = o.teacherStaffId ? employees[o.teacherStaffId] : null;
              const subjectName = subject?.title || subject?.name || `Subject #${o.subjectId}`;
              return (
                <div key={o.id} className={s.offeringRow}>
                  <div className={s.offeringLeft}>
                    <span className={s.offeringSubject}>{subjectName}</span>
                    {o.schedule && (
                      <span className={s.offeringSchedule}>{o.schedule}</span>
                    )}
                  </div>
                  {teacher && (
                    <span className={s.offeringTeacher}>{empName(teacher)}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!hasContent && (
        <div className={s.expandLoading}>No additional details on record for this section.</div>
      )}

      {/* View full section button */}
      {sec && (
        <div className={s.detailFooter}>
          <button
            type="button"
            className={s.viewSectionBtn}
            onClick={onOpenSection}>
            View Full Section Profile →
          </button>
        </div>
      )}
    </div>
  );
}
